import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import tripPlannerGlossaryMessages from 'tripPlanner/i18n/tripPlannerGlossaryMessages'
import AssistChip from 'LuxKit/Chips/AssistChip'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlusIcon from 'LuxKit/Icons/line/LinePlusIcon'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import AddToTripDropdown from './AddToTripDropdown'
import { sendAddPlaceToDay } from './addToTripActions'
import type { TripItineraryData, TripStop, TripDay } from 'tripPlanner/api/types'
import { isPlaceInItinerary, getItineraryDayLabel, findPlaceStopInItinerary, findOfferStopDetails, findNearestStop, type Coordinates } from 'tripPlanner/utils/chatItinerary'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import { useChatStreamStatus } from 'tripPlanner/assistant/contexts/ChatStreamStatusContext'

interface MatchingSelectedDateDay {
  stop: TripStop
  day: TripDay
}

function findUniqueMatchingDayForDate(stops: ReadonlyArray<TripStop>, selectedDate: string): MatchingSelectedDateDay | undefined {
  const matchingDaysByDate = stops
    .flatMap(stop => stop.days.map(day => ({ stop, day })))
    .reduce((daysByDate, matchingDay) => {
      if (!matchingDay.day.date) return daysByDate

      daysByDate.set(
        matchingDay.day.date,
        daysByDate.has(matchingDay.day.date) ? undefined : matchingDay,
      )

      return daysByDate
    }, new Map<string, MatchingSelectedDateDay | undefined>())

  return matchingDaysByDate.get(selectedDate)
}

interface Props {
  itineraryData: TripItineraryData
  onSendMessage: (message: string, options?: SendMessageOptions) => void
  placeName: string
  placeId?: string
  offerId?: string
  offerType?: string
  coordinates?: Coordinates
  variant?: 'filled' | 'outline' | 'button'
  buttonKind?: 'primary' | 'secondary'
  onAfterAction?: () => void
  /** When set and it matches an itinerary day, "Add to trip" adds straight to that day instead of opening the day picker. */
  selectedDate?: string
}

function AddToTripAction({
  itineraryData,
  onSendMessage,
  placeName,
  placeId,
  offerId,
  offerType,
  coordinates,
  variant = 'outline',
  buttonKind = 'primary',
  onAfterAction,
  selectedDate,
}: Props) {
  const intl = useIntl()
  const addToTripLabel = intl.formatMessage(tripPlannerGlossaryMessages.addToTrip)

  const [isOpen, setIsOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const offerStopDetails = useMemo(
    () => findOfferStopDetails(offerId, itineraryData),
    [offerId, itineraryData],
  )

  const placeInItinerary = useMemo(
    () => !!offerStopDetails || isPlaceInItinerary(placeId, itineraryData),
    [offerStopDetails, placeId, itineraryData],
  )

  const itineraryDayLabel = useMemo(() => {
    if (offerStopDetails) {
      return offerStopDetails.dayNumber !== undefined
        ? getLocalisedDayLabel(intl, offerStopDetails.dayNumber, offerStopDetails.dayDate)
        : intl.formatMessage({
            id: 'MgK5ISSCJKWklR8eg12e',
            defaultMessage: 'To be planned',
            description: 'Label for unassigned trip itinerary items that have no scheduled day',
          })
    }
    return getItineraryDayLabel(placeId, itineraryData)
  }, [offerStopDetails, placeId, itineraryData, intl])

  const placeStopInfo = useMemo(
    () => findPlaceStopInItinerary(placeId, itineraryData),
    [placeId, itineraryData],
  )

  const nearestStop = useMemo(
    () => findNearestStop(coordinates, itineraryData?.stops ?? []),
    [coordinates, itineraryData?.stops],
  )

  const initialStop = offerStopDetails?.stop ?? placeStopInfo?.stop ?? nearestStop

  const messageOptions = useMemo<SendMessageOptions | undefined>(
    () => offerId ? { offerId, offerType } : undefined,
    [offerId, offerType],
  )

  // Only skip the picker when the selected date maps to a clear target day.
  const matchingDay = useMemo((): MatchingSelectedDateDay | undefined => {
    if (placeInItinerary || !selectedDate) return undefined

    if (initialStop) {
      const day = findUniqueMatchingDayForDate([initialStop], selectedDate)
      if (day) return day
    }

    return findUniqueMatchingDayForDate(itineraryData.stops, selectedDate)
  }, [placeInItinerary, selectedDate, itineraryData?.stops, initialStop])

  const { isStreaming } = useChatStreamStatus()

  const handleTriggerClick = useCallback(() => {
    if (matchingDay) {
      sendAddPlaceToDay({
        onSendMessage,
        intl,
        placeName,
        placeId,
        stop: matchingDay.stop,
        day: matchingDay.day,
        sendMessageOptions: messageOptions,
      })
      onAfterAction?.()
      return
    }
    setIsOpen(prev => !prev)
  }, [matchingDay, onSendMessage, intl, placeName, placeId, messageOptions, onAfterAction])

  const closeDropdown = useCallback(() => {
    setIsOpen(false)
  }, [])

  const label = placeInItinerary ? itineraryDayLabel : addToTripLabel
  const triggerProps = {
    ref: triggerRef,
    onClick: handleTriggerClick,
    startIcon: placeInItinerary ? undefined : <LinePlusIcon />,
    endIcon: placeInItinerary ? <LineAngleDownIcon /> : undefined,
    disabled: isStreaming,
  }

  const trigger = variant === 'button'
    ? <TextButton kind={buttonKind} size="large" {...triggerProps}>{label}</TextButton>
    : <AssistChip variant={variant} size="small" {...triggerProps}>{label}</AssistChip>

  return (
    <>
      {trigger}
      <AddToTripDropdown
        open={isOpen}
        onClose={closeDropdown}
        triggerRef={triggerRef}
        anchorRef={triggerRef}
        itineraryData={itineraryData}
        placeName={placeName}
        placeId={placeId}
        onSendMessage={onSendMessage}
        sendMessageOptions={messageOptions}
        onAfterAction={onAfterAction}
        initialStop={initialStop}
        currentDayNumber={offerStopDetails?.dayNumber ?? placeStopInfo?.dayNumber}
      />
    </>
  )
}

export default AddToTripAction
